"use client";

import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getFriends,
  getFriendRequests,
  getFriendStatus,
  getFriendsOnline,
  getFriendTeammateIds,
  getFriendsActivity,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  cancelFriendRequest,
  removeFriend,
  updateFriendNote,
  updateFriendCategory,
  inviteFriendToTeam,
  compareWithFriend,
  getTournamentFriends,
  getTournamentsWithFriends,
  type TeamRole,
} from "@/shared/api/friends";
import { usePresenceStore } from "@/shared/model/presence-store";
import { getTeamsTeamidInvitationsQueryKey } from "@/shared/api/generated-kubb/hooks/useGetTeamsTeamidInvitations";
import { getTeamsUserTeamInvitationsQueryKey } from "@/shared/api/generated-kubb/hooks/useGetTeamsUserTeamInvitations";

export const friendsQueryKey = () => ["friends"] as const;
export const friendTeammateIdsQueryKey = () => ["friends", "teammate-ids"] as const;
export const friendsOnlineQueryKey = () => ["friends", "online"] as const;
export const friendRequestsQueryKey = () => ["friends", "requests"] as const;
export const friendStatusQueryKey = (userId: string) => ["friends", "status", userId] as const;
export const compareStatsQueryKey = (userId: string, otherUserId: string) =>
  ["user-stats", "compare", userId, otherUserId] as const;
export const tournamentFriendsQueryKey = (tournamentId: string) =>
  ["tournaments", tournamentId, "friends"] as const;
export const tournamentsWithFriendsQueryKey = () => ["tournaments", "with-friends"] as const;
export const friendsActivityQueryKey = (page: number, pageSize: number) =>
  ["friends", "activity", page, pageSize] as const;

export function useFriends(enabled = true) {
  return useQuery({
    queryKey: friendsQueryKey(),
    queryFn: getFriends,
    enabled,
  });
}

export function useFriendTeammateIds(enabled = true) {
  return useQuery({
    queryKey: friendTeammateIdsQueryKey(),
    queryFn: getFriendTeammateIds,
    enabled,
  });
}

export function useFriendRequests(enabled = true) {
  return useQuery({
    queryKey: friendRequestsQueryKey(),
    queryFn: getFriendRequests,
    enabled,
  });
}

export function useFriendStatus(otherUserId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: friendStatusQueryKey(otherUserId ?? ""),
    queryFn: () => getFriendStatus(otherUserId!),
    enabled: enabled && !!otherUserId,
  });
}

export function useFriendsPresence(enabled: boolean) {
  const setUserStatus = usePresenceStore((s) => s.setUserStatus);
  const setActivityContext = usePresenceStore((s) => s.setActivityContext);
  const query = useQuery({
    queryKey: friendsOnlineQueryKey(),
    queryFn: getFriendsOnline,
    enabled,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (!query.data) return;
    for (const p of query.data) {
      setUserStatus(p.userId, p.status, null);
      setActivityContext(p.userId, p.activityContext ?? null);
    }
  }, [query.data, setUserStatus, setActivityContext]);
  return query;
}

export function useSendFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addresseeUserId: string) => sendFriendRequest(addresseeUserId),
    onSuccess: (_, addresseeUserId) => {
      qc.invalidateQueries({ queryKey: friendStatusQueryKey(addresseeUserId) });
      qc.invalidateQueries({ queryKey: friendRequestsQueryKey() });
    },
  });
}

export function useAcceptFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: acceptFriendRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: friendsQueryKey() });
      qc.invalidateQueries({ queryKey: friendRequestsQueryKey() });
    },
  });
}

export function useDeclineFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: declineFriendRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: friendRequestsQueryKey() });
    },
  });
}

export function useCancelFriendRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cancelFriendRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: friendRequestsQueryKey() });
      qc.invalidateQueries({ queryKey: ["friends"] });
    },
  });
}

export function useRemoveFriend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: removeFriend,
    onSuccess: (_, friendId) => {
      qc.invalidateQueries({ queryKey: friendsQueryKey() });
      qc.invalidateQueries({ queryKey: friendStatusQueryKey(friendId) });
    },
  });
}

export function useUpdateFriendNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ friendId, note }: { friendId: string; note: string | null }) =>
      updateFriendNote(friendId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: friendsQueryKey() });
    },
  });
}

export function useUpdateFriendCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ friendId, category }: { friendId: string; category: number }) =>
      updateFriendCategory(friendId, category),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: friendsQueryKey() });
    },
  });
}

export function useInviteFriendToTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      friendId,
      teamId,
      role,
    }: {
      friendId: string;
      teamId: string;
      role?: TeamRole;
    }) => inviteFriendToTeam(friendId, teamId, role),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: getTeamsTeamidInvitationsQueryKey(variables.teamId),
      });
      qc.invalidateQueries({ queryKey: getTeamsUserTeamInvitationsQueryKey() });
    },
  });
}

export function useCompareStats(userId: string | undefined, otherUserId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: compareStatsQueryKey(userId ?? "", otherUserId ?? ""),
    queryFn: () => compareWithFriend(userId!, otherUserId!),
    enabled: enabled && !!userId && !!otherUserId && userId !== otherUserId,
  });
}

export function useTournamentFriends(tournamentId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: tournamentFriendsQueryKey(tournamentId ?? ""),
    queryFn: () => getTournamentFriends(tournamentId!),
    enabled: enabled && !!tournamentId,
    staleTime: 60_000,
  });
}

export function useTournamentsWithFriends(enabled: boolean) {
  return useQuery({
    queryKey: tournamentsWithFriendsQueryKey(),
    queryFn: getTournamentsWithFriends,
    enabled,
    staleTime: 60_000,
  });
}

export function useFriendsActivity(page = 1, pageSize = 20, enabled = true) {
  return useQuery({
    queryKey: friendsActivityQueryKey(page, pageSize),
    queryFn: () => getFriendsActivity(page, pageSize),
    enabled,
    staleTime: 60_000,
  });
}
